/**
 * Umami tracking helpers.
 *
 * Two things happen for a tracked interaction:
 *
 *  1. A *custom event* with properties — segmentable in Umami's Events report
 *     ("of everyone who clicked the hero CTA, how many were on mobile?").
 *  2. A *virtual pageview* — shows up in Paths and can be used as a step in
 *     Umami's funnel builder, the same way /pricing/checkout/ already is.
 *
 * Every call is a no-op when window.umami is missing (SSR, ad blockers,
 * script not yet loaded), so callers never need to guard.
 *
 * Naming conventions — see docs/tracking-plan.md:
 *   events        Title Case with a leading noun   e.g. "CTA click"
 *   virtual paths lowercase, slashes, trailing /   e.g. "/cta/hero/"
 */

const isTrackable = () => typeof window !== 'undefined' && typeof window.umami !== 'undefined';

/** Hosts we own and can therefore hand a `ref` to. */
const OWNED_HOSTS = ['dashboard.polypane.app', 'staging.dashboard.polypane.app'];

/**
 * Append `?ref=<location>` to an outbound link to one of our own apps.
 *
 * Umami sessions already carry across polypane.app and dashboard.polypane.app,
 * but the session alone can't say *which button* sent someone to the register
 * form. `ref` closes that gap: the register page reads it and reports it on
 * `Register view`, so signup attempts become attributable to a specific CTA.
 *
 * Left untouched: internal links, third-party links, and any URL that already
 * carries a `ref` (an explicit one always wins).
 *
 * Built with string manipulation rather than `new URL()` so it is safe during
 * SSR, where there is no document to resolve relative URLs against.
 */
export const withRef = (to, ref) => {
  if (!to || !ref) return to;
  if (!/^https?:\/\//i.test(to)) return to;

  const host = to.replace(/^https?:\/\//i, '').split(/[/?#]/)[0];
  if (!OWNED_HOSTS.includes(host.toLowerCase())) return to;

  const [base, hash = ''] = to.split('#');
  if (/[?&]ref=/.test(base)) return to;

  return `${base}${base.includes('?') ? '&' : '?'}ref=${encodeURIComponent(ref)}${hash ? `#${hash}` : ''}`;
};

/**
 * Seconds since this page was shown.
 *
 * `window.polypaneRouteStart` is set on initial render and reset on every
 * route change in gatsby-browser.js, so this is time on the *current* page
 * rather than time since the original page load. Falls back to the navigation
 * timing origin if the hook hasn't run yet.
 */
export const secondsOnPage = () => {
  if (typeof window === 'undefined') return undefined;

  if (window.polypaneRouteStart) {
    return Math.round((Date.now() - window.polypaneRouteStart) / 1000);
  }

  return typeof performance !== 'undefined' ? Math.round(performance.now() / 1000) : undefined;
};

/**
 * Fire a custom event with properties.
 *
 * Umami only accepts string/number/boolean property values, so anything else
 * is coerced. Undefined and null values are dropped rather than sent as "null".
 *
 * @param {string} name  Event name, e.g. "CTA click"
 * @param {object} props Flat object of event properties
 */
export const trackEvent = (name, props = {}) => {
  if (!isTrackable()) return;

  const clean = Object.entries(props).reduce((acc, [key, value]) => {
    if (value === undefined || value === null) return acc;
    acc[key] = typeof value === 'object' ? JSON.stringify(value) : value;
    return acc;
  }, {});

  try {
    window.umami.track(name, clean);
  } catch (e) {
    // Never let analytics break the page.
  }
};

/**
 * Fire a virtual pageview so the interaction appears in Paths and can be used
 * as a funnel step.
 *
 * @param {string} url   Virtual path, e.g. "/cta/hero/"
 * @param {string} title Human readable title for the Pages report
 */
export const trackVirtualPageview = (url, title) => {
  if (!isTrackable()) return;

  try {
    window.umami.track((data) => ({ ...data, url, title }));
  } catch (e) {
    // Never let analytics break the page.
  }
};

/**
 * Track a call to action click.
 *
 * Fires both an event and a virtual pageview, because the two answer different
 * questions: the event tells you who clicked, the virtual pageview lets you put
 * the click in a funnel alongside /register/ and /confirm-your-account.
 *
 * @param {string} location Where on the page the CTA lives, e.g. "hero"
 * @param {object} options
 * @param {string} [options.label]       Visible button text
 * @param {string} [options.destination] Where the click goes
 */
export const trackCTA = (location, { label, destination } = {}) => {
  const page = typeof window !== 'undefined' ? window.location.pathname : undefined;

  trackEvent('CTA click', { location, label, destination, page, seconds: secondsOnPage() });
  trackVirtualPageview(`/cta/${location}/`, `CTA: ${location}`);
};

/**
 * Track a click on an internal "read this next" link.
 *
 * Deliberately not `trackCTA`: these links go deeper into the site rather than
 * towards the trial, so they don't belong in the CTA funnel or the /cta/
 * virtual path namespace. `location` keeps separate destinations comparable if
 * the block ever offers more than blog posts.
 *
 * @param {string} location Which block the link sits in, e.g. "related-post"
 * @param {object} options
 * @param {string} [options.destination] Where the click goes
 * @param {string} [options.label]       Link text
 */
export const trackRelated = (location, { destination, label } = {}) => {
  const page = typeof window !== 'undefined' ? window.location.pathname : undefined;

  trackEvent('Related click', { location, destination, label, page, seconds: secondsOnPage() });
};

export default trackEvent;
